<script lang="ts">
  import type { PodInfo } from "@antares/shared";

  interface Props {
    pod: PodInfo;
  }

  let { pod }: Props = $props();

  const statusColor = $derived(
    pod.status === "Running" ? "success" :
    pod.status === "Failed" ? "danger" :
    pod.status === "Pending" ? "warning" : "muted"
  );

  const readyCount = $derived(pod.containers.filter(c => c.ready).length);
  const totalCount = $derived(pod.containers.length);

  function timeSince(dateStr: string): string {
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  }

  function formatMem(bytes: number): string {
    const mb = bytes / (1024 * 1024);
    if (mb < 1024) return `${Math.round(mb)}Mi`;
    return `${(mb / 1024).toFixed(1)}Gi`;
  }
</script>

<a href="#/pods/{pod.namespace}/{pod.name}" class="pod-card" class:monitored={pod.mode === "monitored"}>
  <div class="pod-card-header">
    <span class="pod-name">{pod.displayName}</span>
    <div class="header-right">
      {#if pod.mode === "monitored"}
        <span class="mode-badge">monitor</span>
      {/if}
      <span class="status-dot {statusColor}"></span>
    </div>
  </div>
  <div class="pod-meta">
    <span>{pod.namespace}/{pod.name}</span>
    <span>{readyCount}/{totalCount} ready</span>
    <span>{timeSince(pod.createdAt)} ago</span>
  </div>
  <div class="pod-footer">
    <span class="pod-status">{pod.status}</span>
    {#if pod.metrics}
      <div class="pod-metrics">
        <span class="metric">{pod.metrics.cpuMilliCores}m cpu</span>
        <span class="metric">{formatMem(pod.metrics.memoryBytes)} mem</span>
      </div>
    {/if}
  </div>
</a>

<style>
  .pod-card {
    display: block;
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 1rem;
    text-decoration: none;
    color: inherit;
    transition: border-color 0.15s;
  }

  .pod-card:hover {
    border-color: var(--accent);
  }

  .pod-card.monitored {
    border-color: rgba(139, 92, 246, 0.3);
  }

  .pod-card.monitored:hover {
    border-color: rgba(139, 92, 246, 0.6);
  }

  .pod-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;
  }

  .pod-name {
    font-family: var(--font-mono);
    font-size: 0.9rem;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }

  .mode-badge {
    font-size: 0.6rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.1rem 0.35rem;
    border-radius: 3px;
    background: rgba(139, 92, 246, 0.15);
    color: rgb(139, 92, 246);
    font-weight: 600;
  }

  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .status-dot.success { background: var(--success); }
  .status-dot.danger { background: var(--danger); }
  .status-dot.warning { background: var(--warning); }
  .status-dot.muted { background: var(--text-muted); }

  .pod-meta {
    display: flex;
    gap: 1rem;
    font-size: 0.75rem;
    color: var(--text-muted);
    margin-bottom: 0.5rem;
  }

  .pod-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .pod-status {
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  .pod-metrics {
    display: flex;
    gap: 0.75rem;
  }

  .metric {
    font-size: 0.7rem;
    font-family: var(--font-mono);
    color: var(--text-muted);
    background: var(--bg-elevated);
    padding: 0.1rem 0.4rem;
    border-radius: 3px;
  }
</style>
